import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { closetItems } from '../data/mockData';

const Closet = () => {
  const [filter, setFilter] = useState('all');
  const [isUploading, setIsUploading] = useState(false);
  const [items, setItems] = useState(closetItems);

  const filters = ['all', 'top', 'bottom', 'outerwear', 'shoes', 'accessory'];

  const filteredItems = items.filter(item => filter === 'all' || item.type === filter);

  const handleUploadMock = () => {
    setIsUploading(true);
    setTimeout(() => {
      const newItem = {
        id: Date.now().toString(),
        type: 'top',
        name: 'Structured Blazer',
        brand: 'Balenciaga',
        color: 'Black',
        tags: ['new', 'formal'],
        image: 'https://images.unsplash.com/photo-1591369822096-ffd140ec948f?w=500&q=80',
        wornCount: 0
      };
      setItems([newItem, ...items]);
      setIsUploading(false);
    }, 2500);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-editorial text-3xl">Digital Archive</h1>
          <p className="text-sm text-graphite-500 font-mono mt-1">TOTAL ASSETS: {items.length}</p>
        </div>
        <button 
          onClick={handleUploadMock}
          disabled={isUploading}
          className="btn-primary flex items-center gap-2"
        >
          {isUploading ? (
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}>
              <SafeIcon name="Loader" className="w-4 h-4" />
            </motion.div>
          ) : (
            <SafeIcon name="Plus" className="w-4 h-4" />
          )}
          {isUploading ? 'ANALYZING...' : 'ADD ITEM'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 text-xs font-mono uppercase tracking-wider whitespace-nowrap transition-colors ${
              filter === f 
                ? 'bg-graphite-900 text-white' 
                : 'bg-white border border-pearl-200 text-graphite-600 hover:border-graphite-400'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Grid */}
      <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        <AnimatePresence>
          {filteredItems.map(item => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              key={item.id}
              className="group cursor-pointer"
            >
              <div className="aspect-[3/4] bg-white border border-pearl-200 relative overflow-hidden mb-2">
                <img 
                  src={item.image} 
                  alt={item.name} 
                  className="w-full h-full object-cover p-4 mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-4">
                  <div className="flex justify-end gap-2">
                    <button className="w-8 h-8 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors">
                      <SafeIcon name="Heart" className="w-4 h-4" />
                    </button>
                    <button className="w-8 h-8 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white hover:text-black transition-colors">
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-white text-xs font-mono">
                    WORN {item.wornCount}x
                  </div>
                </div>
              </div>
              <div>
                <p className="font-semibold text-sm truncate">{item.brand}</p>
                <p className="text-xs text-graphite-500 truncate">{item.name}</p>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Uploading Overlay Modal Mock */}
      <AnimatePresence>
        {isUploading && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-pearl-50/80 backdrop-blur-sm"
          >
            <div className="bg-white border border-pearl-200 shadow-2xl p-8 max-w-sm w-full text-center space-y-6">
              <div className="relative w-24 h-24 mx-auto">
                <motion.div 
                  className="absolute inset-0 border-2 border-runway-500 rounded-full border-t-transparent"
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                />
                <SafeIcon name="Camera" className="w-8 h-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-graphite-400" />
              </div>
              <div>
                <h3 className="font-editorial text-xl mb-2">Analyzing Garment</h3>
                <p className="text-xs text-graphite-500 font-mono">EXTRACTING METADATA • REMOVING BACKGROUND • TAGGING STYLE</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Closet;